import { isSealedField, openField, openJson, type ContentField, type ContentKey } from '../../shared/contentCrypto';
import { t } from '../language';
import { getContentKey } from './contentKey';

/**
 * Veritabanından okunan toplantı satırlarındaki şifreli alanları çözer (tn1).
 * Şifreli değer yoksa satır olduğu gibi döner ve anahtar hiç istenmez — eski
 * (düz) kayıtlarla şifreli kayıtlar bir arada yaşayabilir.
 *
 * Tanınan yerler (veri koruma programı §5.4):
 *   meetings: title, summary, key_points, options.decisions, options.calendarAttendees,
 *             options.clientTasks.summaryEmail, options.clientTasks.participants
 *   transcripts[]: segments, full_text · action_items[]: text, assignee
 *   craft_blocks[]: content · meeting_comments[]: body · options.speakerMap
 *   meeting_participants[]: name, email, role, company (bağlam: toplantı)
 *   people / companies satırları: openPersonRow / openCompanyRow (bağlam: satırın kendisi)
 *
 * Bir alan çözülemezse (anahtar alınamadı, değer bozuk) arayüz çökmesin diye
 * yerine "açılamadı" yazısı konur ve günlüğe düşülür.
 */

type Row = Record<string, any>;

/**
 * Gömülü `transcripts ( segments )` alanının segmentleri. transcripts.meeting_id benzersiz
 * olduğu için PostgREST bire bir ilişkiyi tek nesne olarak döndürür (eski sürümler dizi
 * döndürüyordu) — ikisi de kabul edilir.
 */
export function embeddedSegments(transcripts: unknown): unknown {
  const row = Array.isArray(transcripts) ? transcripts[0] : transcripts;
  return row && typeof row === 'object' ? (row as Row).segments : undefined;
}

const UNREADABLE = () => t('[Şifreli içerik açılamadı]', '[Encrypted content could not be opened]');

function hasSealed(value: unknown): boolean {
  if (isSealedField(value)) return true;
  if (Array.isArray(value)) return value.some(hasSealed);
  if (value && typeof value === 'object') return Object.values(value).some(hasSealed);
  return false;
}

type Opener = (field: ContentField, value: unknown, json: boolean) => Promise<unknown>;

function makeOpener(key: ContentKey | null, userId: string, meetingId: string): Opener {
  return async (field, value, json) => {
    if (!isSealedField(value)) return value;
    try {
      if (!key) throw new Error('anahtar yok');
      const ctx = { userId, meetingId, field };
      return json ? await openJson(key, ctx, value) : await openField(key, ctx, value);
    } catch (err) {
      console.error(`[crypto] ${field} çözülemedi (${meetingId.slice(0, 8)}):`, (err as Error).message);
      if (field === 'meetings.summary_email') return undefined;
      if (field === 'meetings.participants') return [];
      if (field === 'meetings.speaker_map') return {};
      if (field.endsWith('.email') || field.endsWith('.role') || field.endsWith('.domain') || field.endsWith('.notes') || field === 'meeting_participants.company') return null;
      return json ? [] : UNREADABLE();
    }
  };
}

async function keyFor(userId: string): Promise<ContentKey | null> {
  try {
    return await getContentKey(userId);
  } catch (err) {
    console.error('[crypto] içerik anahtarı alınamadı:', (err as Error).message);
    return null;
  }
}

/** Gömülü alt satırlar: dizi ya da (bire bir ilişkide, ör. transcripts) tek nesne. */
async function openChildren(rows: unknown, fields: Array<[string, ContentField, boolean]>, open: Opener): Promise<unknown> {
  const openOne = async (child: Row) => {
    const next = { ...child };
    for (const [column, field, json] of fields) {
      if (column in next) next[column] = await open(field, next[column], json);
    }
    return next;
  };
  if (Array.isArray(rows)) return Promise.all(rows.map(openOne));
  if (rows && typeof rows === 'object') return openOne(rows as Row);
  return rows;
}

/** Toplantı satırını (gömülü alt tablolarıyla) çözer. Satırda `id` olmalı. */
export async function openMeetingRow<T extends Row>(row: T, userId: string): Promise<T> {
  if (!hasSealed(row)) return row;
  const open = makeOpener(await keyFor(userId), userId, row.id as string);
  const next: Row = { ...row };

  if ('title' in next) next.title = await open('meetings.title', next.title, false);
  if ('summary' in next) next.summary = await open('meetings.summary', next.summary, false);
  if ('key_points' in next) next.key_points = await open('meetings.key_points', next.key_points, true);
  if (next.options && typeof next.options === 'object') {
    const options = { ...next.options };
    if ('decisions' in options) options.decisions = await open('meetings.decisions', options.decisions, true);
    if ('calendarAttendees' in options) options.calendarAttendees = await open('meetings.participants', options.calendarAttendees, true);
    if ('speakerMap' in options) options.speakerMap = await open('meetings.speaker_map', options.speakerMap, true);
    if (options.clientTasks && typeof options.clientTasks === 'object') {
      const tasks = { ...options.clientTasks };
      if ('summaryEmail' in tasks) tasks.summaryEmail = await open('meetings.summary_email', tasks.summaryEmail, true);
      if ('participants' in tasks) tasks.participants = await open('meetings.participants', tasks.participants, true);
      options.clientTasks = tasks;
    }
    next.options = options;
  }
  if ('transcripts' in next) {
    next.transcripts = await openChildren(next.transcripts, [['segments', 'transcripts.segments', true], ['full_text', 'transcripts.full_text', false]], open);
  }
  if ('action_items' in next) {
    next.action_items = await openChildren(next.action_items, [['text', 'action_items.text', false], ['assignee', 'action_items.assignee', false]], open);
  }
  if ('craft_blocks' in next) next.craft_blocks = await openChildren(next.craft_blocks, [['content', 'craft_blocks.content', false]], open);
  if ('meeting_comments' in next) next.meeting_comments = await openChildren(next.meeting_comments, [['body', 'meeting_comments.body', false]], open);
  if ('meeting_participants' in next) next.meeting_participants = await openChildren(next.meeting_participants, PARTICIPANT_FIELDS, open);
  return next as T;
}

const PARTICIPANT_FIELDS: Array<[string, ContentField, boolean]> = [
  ['name', 'meeting_participants.name', false],
  ['email', 'meeting_participants.email', false],
  ['role', 'meeting_participants.role', false],
  ['company', 'meeting_participants.company', false],
];
const PERSON_FIELDS: Array<[string, ContentField, boolean]> = [
  ['name', 'people.name', false],
  ['email', 'people.email', false],
  ['role', 'people.role', false],
];
const COMPANY_FIELDS: Array<[string, ContentField, boolean]> = [
  ['name', 'companies.name', false],
  ['domain', 'companies.domain', false],
  ['notes', 'companies.notes', false],
];

/** Tek başına okunan katılımcı satırları (bağlam: toplantı). */
export async function openParticipantRows<T extends Row>(rows: T[], userId: string, meetingId: string): Promise<T[]> {
  if (!hasSealed(rows)) return rows;
  return (await openChildren(rows, PARTICIPANT_FIELDS, makeOpener(await keyFor(userId), userId, meetingId))) as T[];
}

async function openOwnRow<T extends Row>(row: T, userId: string, fields: Array<[string, ContentField, boolean]>): Promise<T> {
  if (!hasSealed(row)) return row;
  return (await openChildren(row, fields, makeOpener(await keyFor(userId), userId, row.id as string))) as T;
}

/**
 * Kişi satırı (people) — gömülü `companies` (tek nesne ya da dizi) de çözülür. Satırda ve
 * gömülü şirkette `id` olmalı: şifre bağlamı satırın kendisi.
 */
export async function openPersonRow<T extends Row>(row: T, userId: string): Promise<T> {
  const next: Row = await openOwnRow(row, userId, PERSON_FIELDS);
  if (next.companies) {
    next.companies = Array.isArray(next.companies)
      ? await Promise.all(next.companies.map((company: Row) => openOwnRow(company, userId, COMPANY_FIELDS)))
      : await openOwnRow(next.companies, userId, COMPANY_FIELDS);
  }
  return next as T;
}

/** Klasör adı; şifre bağlamı klasörün kimliği. Düz adlar olduğu gibi döner. */
export async function openFolderName(name: string, userId: string, folderId: string): Promise<string> {
  if (!isSealedField(name)) return name;
  return String(await makeOpener(await keyFor(userId), userId, folderId)('meeting_folders.name', name, false));
}

/** Şirket satırı (companies) — gömülü `people` dizisi de çözülür. */
export async function openCompanyRow<T extends Row>(row: T, userId: string): Promise<T> {
  const next: Row = await openOwnRow(row, userId, COMPANY_FIELDS);
  if (Array.isArray(next.people)) next.people = await Promise.all(next.people.map((person: Row) => openOwnRow(person, userId, PERSON_FIELDS)));
  return next as T;
}

export function openMeetingRows<T extends Row>(rows: T[], userId: string): Promise<T[]> {
  return Promise.all(rows.map((row) => openMeetingRow(row, userId)));
}

/** Tek başına okunan transkript (transcripts.segments). */
export async function openTranscriptSegments(stored: unknown, userId: string, meetingId: string): Promise<unknown> {
  if (!isSealedField(stored)) return stored;
  return makeOpener(await keyFor(userId), userId, meetingId)('transcripts.segments', stored, true);
}
